import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { providersPathTo } from '../utils/Api';
import SnackbarContext from '../contexts/SnackbarContext';
import { SearchContext } from '../contexts/SearchContext';
import { Avatar, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { apiBaseUrl } from '../utils/Api/Api';
import { ShoppingCartProductsContext } from '../contexts/ShoppingCartProductsContext';
import ProductsTagFilters from './ProductsTagFilters';
import { ArrowBackRounded, FavoriteBorderRounded, FavoriteRounded, Share } from '@mui/icons-material';
import { ShopProductList } from '.';
import ShoppingCartElement from './building_blocks/ShoppingCartElement';
import { layoutIconButtonRadius, layoutShopSheetRadius } from '../theme/colors';
import { useProductTagFilter } from '../hooks/useProductTagFilter';
import { addFavoriteProvider, isFavoriteProvider, removeFavoriteProvider } from '../utils/providerFavoritesStorage';
import { copyPageLink } from '../utils/copyPageLink';

function ProviderShop() {
  const { id } = useParams();
  const [provider, setProvider] = useState();
  const [isFavorite, setIsFavorite] = useState(false);
  const { tagFilter, setTagFilter, catalogTags, filteredProducts } = useProductTagFilter(provider?.products);
  const { searchTags, fromSearch, setFromSearch } = useContext(SearchContext);
  const { shoppingCartProducts, setShoppingCartProducts } = useContext(ShoppingCartProductsContext);
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SnackbarContext);

  useEffect(() => {
    if (searchTags.length > 0 && fromSearch) {
      setTagFilter(searchTags);
      setFromSearch(false);
    }
  }, [searchTags, fromSearch, setTagFilter, setFromSearch]);

  const handleAddProduct = (product, index) => {
    if (shoppingCartProducts.length > 0 && shoppingCartProducts[0].provider_id !== product.provider_id) {
      openSnackbar('Você não pode adicionar produtos de lojas diferentes ao carrinho', 'info');
      return;
    }
    const newCartProduct = {
      ...product,
      cartQuantity: 1,
      productIndex: index
    };
    setShoppingCartProducts([...shoppingCartProducts, newCartProduct]);
    localStorage.setItem('shoppingCartProducts', JSON.stringify([...shoppingCartProducts, newCartProduct]));
    localStorage.setItem('shoppingProvider', JSON.stringify(provider));
  };

  const changeCartQuantity = (id, operation) => {
    let newShoppingCartProducts = [...shoppingCartProducts];
    const cartProduct = shoppingCartProducts.find(product => product.id === id);
    if (operation === '+') {
      if (cartProduct.quantity === 0 || (cartProduct.cartQuantity < cartProduct.quantity))
        cartProduct.cartQuantity += 1;
    } else {
      cartProduct.cartQuantity -= 1;
    }
    if (cartProduct.cartQuantity < 1) {
      var index = newShoppingCartProducts.findIndex(item => item.id === id);
      newShoppingCartProducts.splice(index, 1);
    }
    setShoppingCartProducts(newShoppingCartProducts);
    localStorage.setItem('shoppingCartProducts', JSON.stringify(newShoppingCartProducts));
  };

  useEffect(() => {
    if (id) {
      providersPathTo.provider_with_products({ id: id }).then(result => {
        setProvider(result.data);
      });
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    setIsFavorite(isFavoriteProvider(id));
  }, [id]);

  useEffect(() => {
    if (!provider) {
      return;
    }
    document.title = `${provider.name} - TudoPets.app`;
  }, [provider]);

  if (!provider) {
    return <div>Carregando...</div>;
  }

  const handleFavoriteToggle = () => {
    if (isFavorite) {
      removeFavoriteProvider(provider.id);
    } else {
      addFavoriteProvider(provider);
    }

    setIsFavorite(!isFavorite);
    openSnackbar(isFavorite ? 'Loja removida dos favoritos' : 'Loja adicionada aos favoritos', isFavorite ? 'info' : 'success');
  };

  const handleShareClick = async () => {
    try {
      await copyPageLink();
      openSnackbar('Link copiado para a área de transferência', 'success');
    } catch {
      openSnackbar('Não foi possível copiar o link', 'error');
    }
  };

  const favoriteLabel = isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
  const actionButtonSx = {
    width: 40,
    height: 40,
    bgcolor: alpha('#1B1B1B', 0.58),
    color: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
      bgcolor: alpha('#1B1B1B', 0.72),
    },
    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 32px)' }}>
      <Box id="shop-top" sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-start' }}>
        <Button data-no-shadow="true" onClick={() => window.history.back()} sx={{ marginLeft: '-8px', color: 'text.primary', borderRadius: layoutIconButtonRadius }}>
          <ArrowBackRounded />
        </Button>
      </Box>
      <Box id="shop-main" sx={{ position: 'relative', display: 'flex', alignItems: 'center', borderRadius: `${layoutShopSheetRadius} ${layoutShopSheetRadius} 0 0`, bgcolor: 'surface.card', flex: 1, flexDirection: 'column', alignSelf: 'stretch', justifyContent: 'flex-start', width: { xs: 'calc(100% + 32px)', sm: '100%' }, mx: { xs: -2, sm: 0 } }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Compartilhar">
            <IconButton
              aria-label="Compartilhar"
              onClick={handleShareClick}
              sx={actionButtonSx}
            >
              <Share sx={{ transform: 'translateX(-1px)' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={favoriteLabel}>
            <IconButton
              aria-label={favoriteLabel}
              onClick={handleFavoriteToggle}
              sx={(theme) => ({
                ...actionButtonSx,
                '& .MuiSvgIcon-root': {
                  fontSize: 24,
                  filter: isFavorite ? `drop-shadow(0 0 1px ${theme.palette.primary.main})` : 'none',
                },
              })}
            >
              {isFavorite ? <FavoriteRounded sx={{ color: 'primary.main' }} /> : <FavoriteBorderRounded />}
            </IconButton>
          </Tooltip>
        </Box>
        <Avatar sx={{ border: '1px solid lightgray', width: '80px', height: '80px', marginTop: '-32px', bgcolor: '#CFD8DC' }} src={provider.business_logo && `${apiBaseUrl}${provider.business_logo}`} />
        <Box sx={{
          mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
        }}>
          <Typography variant="pageTitle"><b>{provider.name}</b></Typography>
          <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 400, lineHeight: '24px', color: '#546E7A' }}>
            <small>1,5km &#9679; Entrega em até 24h &#9679; R$10,00</small>
          </Typography>
          <Typography color="primary" onClick={() => navigate('/shop_info/', { state: provider })}
            sx={{
              fontFamily: 'Nunito', fontSize: '16px', fontWeight: 400, lineHeight: '14px', textDecorationLine: 'underline',
              textDecorationStyle: 'solid',
              textDecorationSkipInk: 'auto',
              textDecorationThickness: 'auto',
              textUnderlineOffset: '3px',
              textUnderlinePosition: 'from-font',
              cursor: 'pointer'
            }}>Ver perfil</Typography>
        </Box>

        <ProductsTagFilters
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          tags={catalogTags}
          numProducts={filteredProducts.length}
          defaultExpanded={false}
          sx={{ px: 2, width: '100%' }}
        />

        <ShopProductList
          products={filteredProducts}
          shoppingCartProducts={shoppingCartProducts}
          handleAddProduct={handleAddProduct}
          changeCartQuantity={changeCartQuantity}
          provider={provider}
        />
      </Box>
      <ShoppingCartElement />
    </Box>
  );
}

export default ProviderShop;
